import { Space, Table, Tooltip, Button, Modal, Select, notification } from "antd";
import { DeleteFilled, EditFilled, BugFilled } from "@ant-design/icons";
import OrderStatusIcon from "../../order-status-icon";
import PartName from "../../part-name";
import classNames from "classnames";
import { IDrone } from "@drone-parts.garphild.pro/interfaces";
import { useState } from "react";
import ordersStorage from "../../../../../storages/orders.storage";
import { updateDronePart } from "../../../../../queries/drones";
import dayjs from "dayjs";

interface IPartsTabProps {
  drone: IDrone;
  dronePartTypes: Record<string, any[]>;
  mainParts: string[];
  mainDroneParts: any[];
  partOrder: string[];
  onPartUpdated?: (drone: IDrone) => void;
  onShowAddPart?: (show: boolean) => void;
  showAddPart?: boolean;
}

export default function PartsTab({
  drone,
  dronePartTypes,
  mainParts,
  mainDroneParts,
  partOrder,
  onPartUpdated,
  onShowAddPart,
  showAddPart,
}: IPartsTabProps) {
  const [editingPart, setEditingPart] = useState<{ partKey: string; currentItemId?: string } | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [addingPart, setAddingPart] = useState<{ partKey: string } | null>(null);
  const [selectedAddItemId, setSelectedAddItemId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const getAvailableItemsForPart = (partKey: string, currentItemId?: string) => {
    const partTypes = drone.parts.find(p => p.key.toLowerCase() === partKey.toLowerCase())?.item?.partTypes || [];
    
    return ordersStorage
      .productsInStock
      .filter((item) => {
        if (!item.partTypes?.length) return false;
        if (currentItemId && item._id === currentItemId) return true;
        
        const hasMatchingPartType = partTypes.length === 0 || 
          item.partTypes?.some(pt => partTypes.some(dpt => pt.split(':')[0] === dpt.split(':')[0]));
        if (!hasMatchingPartType) return false;
        
        if ((item.inStock || 0) <= 0) return false;
        const usedItemsCount = item.used?.reduce((acc, usedItem) => acc + usedItem.quantity, 0) || 0;
        return (item.inStock || 0) > usedItemsCount;
      })
      .map((item) => {
        const name = item.shortName || `${item.itemText} / ${item.itemVariant}`;
        const inStock = item.inStock || 0;
        const orderDate = dayjs(item.orderDate);
        const usedItemsCount = item.used?.reduce((acc, usedItem) => acc + usedItem.quantity, 0) || 0;
        let label = `${orderDate?.format("YYYY-MM-DD")} - ${name} : ${inStock - usedItemsCount} ($${item.itemPrice?.toFixed(2)})`;
        if (!item.received) {
          label = `⏳ ${label}`;
        }
        return { label, value: item._id };
      });
  };

  const handleEditPart = (partKey: string, currentItemId?: string) => {
    setEditingPart({ partKey, currentItemId });
    setSelectedItemId(currentItemId || null);
  };

  const handleSavePart = async () => {
    if (!editingPart || !selectedItemId || selectedItemId === editingPart.currentItemId) {
      setEditingPart(null);
      return;
    }

    setIsUpdating(true);
    try {
      const result = await updateDronePart({
        droneId: drone._id,
        partKey: editingPart.partKey,
        newItemId: selectedItemId,
        quantity: 1,
      });

      if (result.success && result.drone) {
        notification.success({ message: 'Part updated successfully' });
        onPartUpdated?.(result.drone);
      } else {
        notification.error({ message: 'Failed to update part' });
      }
    } catch (error) {
      notification.error({ message: 'Error updating part' });
    } finally {
      setIsUpdating(false);
      setEditingPart(null);
    }
  };

  const getAvailablePartTypes = () => {
    return mainParts;
  };

  const handleAddPart = (partKey: string) => {
    setAddingPart({ partKey });
    setSelectedAddItemId(null);
  };

  const handleSaveAddPart = async () => {
    if (!addingPart || !selectedAddItemId) {
      setAddingPart(null);
      return;
    }

    setIsAdding(true);
    try {
      const result = await updateDronePart({
        droneId: drone._id,
        partKey: addingPart.partKey,
        newItemId: selectedAddItemId,
        quantity: 1,
      });

      if (result.success && result.drone) {
        notification.success({ message: 'Part added successfully' });
        onPartUpdated?.(result.drone);
      } else {
        notification.error({ message: 'Failed to add part' });
      }
    } catch (error) {
      notification.error({ message: 'Error adding part' });
    } finally {
      setIsAdding(false);
      setAddingPart(null);
    }
  };

  return (
    <>
    <Modal
      title={`Change ${editingPart?.partKey || 'part'}`}
      open={!!editingPart}
      onOk={handleSavePart}
      onCancel={() => setEditingPart(null)}
      confirmLoading={isUpdating}
      width={600}
    >
      <Select
        style={{ width: '100%' }}
        placeholder="Select new item"
        showSearch
        filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
        value={selectedItemId}
        onChange={setSelectedItemId}
        options={editingPart ? getAvailableItemsForPart(editingPart.partKey, editingPart.currentItemId) : []}
      />
    </Modal>
    <Modal
      title={`Add ${addingPart?.partKey || 'part'}`}
      open={!!addingPart}
      onOk={handleSaveAddPart}
      onCancel={() => setAddingPart(null)}
      confirmLoading={isAdding}
      width={600}
    >
      <Select
        style={{ width: '100%' }}
        placeholder="Select item"
        showSearch
        filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
        value={selectedAddItemId}
        onChange={setSelectedAddItemId}
        options={addingPart ? getAvailableItemsForPart(addingPart.partKey) : []}
      />
    </Modal>
    <div style={{ height: '380px', overflow: 'auto', position: 'relative' }}>
      <Space direction="vertical" style={{ width: '100%', position: 'absolute' }}>
        {showAddPart && getAvailablePartTypes().length > 0 && (
          <div style={{ marginBottom: '8px' }}>
            <Select
              placeholder="Add new component"
              style={{ width: '200px' }}
              options={getAvailablePartTypes().map(part => ({ label: part, value: part }))}
              onChange={(partKey) => {
                handleAddPart(partKey);
                onShowAddPart?.(false);
              }}
              allowClear
              onClear={() => onShowAddPart?.(false)}
            />
          </div>
        )}
        <Table
          showHeader={false}
          className="drone-parts-table"
          rootClassName="drone-parts-table__root"
          rowClassName={(record) => {
            if (record?.isDivider) return 'drone-parts-table__divider';
            return classNames({
              'drone-parts-table__row': true,
              'drone-parts-table__row_not-received': !record?.item?.received,
              'drone-parts-table__row_planning': record?.orderStatus?.toLowerCase() === 'planning',
            });
          }}
          onRow={(record) => {
            if (record?.isDivider) {
              return {
                style: { 
                  left: '10%',
                  height: '1px', 
                  width: '100%', 
                  margin: '12px 0', 
                  display: 'inline-flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  fontSize: '0.7rem',
                  color: '#666'
                },
                'data-divider': true,
                'data-divider-text': record.dividerText
              };
            }
            return {};
          }}
          rowHoverable={true}
          columns={[
            {
              title: "Part",
              dataIndex: "item",
              key: "item",
              render: (item, record) => {
                if (record?.isDivider) {
                  return null; // Divider row, will be handled by onRow
                }
                const partType = item?.partTypes?.[0]?.split(':')?.[0];
                return (
                  <Space>
                    <OrderStatusIcon
                      orderStatus={item?.orderStatus}
                      received={item?.received}
                    />
                    <PartName
                      name={partType}
                      received={item?.received}
                      orderObjectId={item?.orderObjectId}
                      isPlanning={item?.orderStatus?.toLowerCase() === 'planning'}
                      marketplace={item?.marketplace}
                    />
                    <span>{item?.shortName || item?.itemText}</span>
                  </Space>
                );
              }
            },
            {
              title: "Price",
              dataIndex: "price",
              key: "price",
              render: (price, rowData) => {
                if (rowData?.isDivider) {
                  return null; // Divider row, will be handled by onRow
                }
                const priceFromDrone = price;
                const priceFromItem = (rowData?.item?.itemPrice) || 0;
                const isPricesSame = Math.abs(priceFromDrone - priceFromItem) < 0.01;
                return (
                  <Tooltip title={(
                    <Space className='drone-parts-table__row_toolbar'>
                      <Button><DeleteFilled title={"Delete component"} /></Button>
                      <Button onClick={() => handleEditPart(rowData?.key, rowData?.itemId)}><EditFilled title={"Edit component"}/></Button>
                      <Button><BugFilled title={"Broken/damaged/not working component"}/></Button>
                    </Space>
                  )}>
                    ${(price * rowData?.quantity).toFixed(2)}{isPricesSame ? '' : ` / ${(priceFromItem * rowData?.quantity)?.toFixed(2)}`}
                    {rowData?.quantity !== 1 ? ` (x${rowData?.quantity})` : ''}
                  </Tooltip>
                );
              }
            },
          ]}
          components={{
            body: {
              row: (props: any) => {
                if (props['data-divider']) {
                  return (
                    <tr {...props} className="ant-table-row ant-table-row-level-0 drone-parts-table__divider">
                      <td colSpan={2} style={{ fontSize: '0.5rem', position: 'absolute', height: '24px', top: '50%', transform: 'translateY(-50%)', width: '100%' }}>
                        <div style={{
                          position: 'absolute',
                          left: '50%',
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: 'white',
                          padding: '0 10px',
                          color: '#707070',
                          fontSize: '10px',
                          fontWeight: 400,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}>
                          {props['data-divider-text']}
                        </div>
                      </td>
                    </tr>
                  );
                }
                return <tr {...props} />;
              },
            },
          }}
          dataSource={(() => {
            const parts = Object.values(dronePartTypes).flat();
            
            // Add a flag to each part indicating if it's in partOrder
            const partsWithType = parts.map(part => ({
              ...part,
              _isInPartOrder: partOrder.some(type => part?.key?.toLowerCase() === type)
            }));
            
            // Sort the parts
            const processedParts = [...partsWithType].sort((a, b) => {
              const getPartType = (part: any): number => {
                const partType = part?.key?.toLowerCase() || '';
                const index = partOrder.findIndex(type => partType === type);
                return index === -1 ? partOrder.length : index;
              };

              const typeA = getPartType(a);
              const typeB = getPartType(b);

              if (typeA !== typeB) {
                return typeA - typeB;
              }
              
              const nameA = a?.key || '';
              const nameB = b?.key || '';
              return nameA.localeCompare(nameB);
            });

            // Add 'Main parts' divider at the beginning
            processedParts.unshift({
              key: 'main-parts-divider',
              isDivider: true,
              dividerText: 'Main parts'
            });

            // Find the index of the last part that is in partOrder (adjusting for the new first divider)
            const lastPartOrderIndex = processedParts.reduce((lastIndex, part, index) => {
              // Skip the first divider when checking _isInPartOrder
              if (index === 0 && part.isDivider) return lastIndex;
              return part._isInPartOrder ? index : lastIndex;
            }, 0); // Start with 0 as the first element is now a divider

            // Insert 'Other' divider if there are parts not in partOrder
            // Ensure lastPartOrderIndex is valid and there are items after it that are not the 'Other' divider itself
            if (lastPartOrderIndex > 0 && lastPartOrderIndex < processedParts.length - 1) {
              // Check if the item after lastPartOrderIndex is not already a divider
              if (!processedParts[lastPartOrderIndex + 1]?.isDivider) {
                processedParts.splice(lastPartOrderIndex + 1, 0, {
                  key: 'other-parts-divider',
                  isDivider: true,
                  dividerText: 'Other'
                });
              }
            }
            return processedParts;
          })()}
          size={"small"}
          pagination={false}
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: 'transparent',
          }}
        />
      </Space>
    </div>
    </>
  );
}
